import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Star, CheckCircle, ThumbsUp, ThumbsDown } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ReviewLanding() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const initialSentiment = searchParams.get("sentiment"); // "happy" or "unhappy"
  
  const [loading, setLoading] = useState(true);
  const [reviewData, setReviewData] = useState(null);
  const [sentiment, setSentiment] = useState(initialSentiment);
  const [rating, setRating] = useState(initialSentiment === "happy" ? 5 : 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [redirectingToGoogle, setRedirectingToGoogle] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReviewData();
  }, [token]);

  // Auto-redirect happy customers to Google after data loads
  useEffect(() => {
    if (reviewData && sentiment === "happy" && !redirectingToGoogle) {
      handleHappyPath();
    }
  }, [reviewData, sentiment]);

  const fetchReviewData = async () => {
    try {
      const response = await fetch(`${API}/public/review/${token}`);
      if (response.ok) {
        const data = await response.json();
        setReviewData(data);
        
        // Track the page view with sentiment
        trackSentiment(initialSentiment);
      } else {
        setError("This review link is invalid or has expired.");
      }
    } catch (error) {
      setError("Unable to load review page. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const trackSentiment = async (sentimentValue) => {
    try {
      await fetch(`${API}/public/review/${token}/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sentiment: sentimentValue }),
      });
    } catch (e) {
      // Ignore tracking errors
    }
  };

  const handleHappyPath = async () => {
    setRedirectingToGoogle(true);
    
    // Track that they're going to Google
    try {
      await fetch(`${API}/public/review/${token}/google-clicked`, {
        method: "POST",
      });
    } catch (e) {
      // Ignore errors
    }
    
    // Small delay to show the redirect message
    setTimeout(() => {
      window.location.href = reviewData.google_business_url;
    }, 1500);
  };

  const submitFeedback = async () => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/public/review/${token}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          rating, 
          feedback,
          sentiment: sentiment || "unhappy"
        }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        toast.error("Failed to submit feedback. Please try again.");
      }
    } catch (error) {
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6600]" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">😕</span>
          </div>
          <h1 className="text-2xl font-bold text-zinc-800 mb-2">Oops!</h1>
          <p className="text-zinc-600">{error}</p>
        </div>
      </div>
    );
  }

  // Redirecting to Google (happy path)
  if (redirectingToGoogle) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ThumbsUp size={48} className="text-emerald-600" weight="fill" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-800 mb-2">Awesome!</h1>
          <p className="text-zinc-600 mb-4">
            We're so glad you had a great experience with {reviewData?.company_name}!
          </p>
          <p className="text-zinc-500 text-sm">
            Taking you to leave a review...
          </p>
          <div className="mt-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  // Feedback submitted (thank you state)
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={48} className="text-emerald-600" weight="fill" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-800 mb-2">Thank You!</h1>
          <p className="text-zinc-600 mb-4">
            We really appreciate you taking the time to share your feedback with {reviewData?.company_name}.
          </p>
          <p className="text-zinc-500 text-sm">
            Your feedback has been sent directly to our team and will help us improve.
          </p>
          <div className="flex justify-center gap-1 mt-6">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                size={32}
                weight={star <= rating ? "fill" : "regular"}
                className={star <= rating ? "text-amber-500" : "text-zinc-300"}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // No sentiment yet - show selection (fallback if URL has no query param)
  if (!sentiment) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white">
        <div className="bg-white border-b border-zinc-200 py-6">
          <div className="max-w-lg mx-auto px-4 text-center">
            <h1 className="text-2xl font-bold text-zinc-800">{reviewData?.company_name}</h1>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 py-8">
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-zinc-800 mb-2">
                Hi {reviewData?.customer_name?.split(" ")[0]}! 👋
              </h2>
              <p className="text-zinc-600">
                How was your experience with {reviewData?.company_name}?
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-8">
              <button
                onClick={() => {
                  setSentiment("happy");
                  trackSentiment("happy");
                }}
                className="flex flex-col items-center justify-center gap-3 p-6 bg-white border-2 border-zinc-200 rounded-xl hover:border-emerald-400 hover:bg-emerald-50 transition-all shadow-sm"
                data-testid="sentiment-happy-btn"
              >
                <span className="text-5xl">😊</span>
                <span className="font-semibold text-zinc-800">Great!</span>
              </button>

              <button
                onClick={() => {
                  setSentiment("unhappy");
                  setRating(0);
                  trackSentiment("unhappy");
                }}
                className="flex flex-col items-center justify-center gap-3 p-6 bg-white border-2 border-zinc-200 rounded-xl hover:border-orange-400 hover:bg-orange-50 transition-all shadow-sm"
                data-testid="sentiment-unhappy-btn"
              >
                <span className="text-5xl">😕</span>
                <span className="font-semibold text-zinc-800">Could be better</span>
              </button>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Unhappy path - show feedback form
  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white">
      <div className="bg-white border-b border-zinc-200 py-6">
        <div className="max-w-lg mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-zinc-800">{reviewData?.company_name}</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ThumbsDown size={32} className="text-orange-500" weight="fill" />
            </div>
            <h2 className="text-xl font-semibold text-zinc-800 mb-2">We're Sorry to Hear That</h2>
            <p className="text-zinc-600">
              Please share your feedback with {reviewData?.company_name}.<br />
              <span className="text-sm text-zinc-500">This will be sent directly to our team, not posted publicly.</span>
            </p>
          </div>

          {/* Star Rating */}
          <div className="bg-white border-2 border-zinc-200 rounded-xl p-6">
            <p className="text-center font-medium text-zinc-700 mb-4">How would you rate your experience?</p>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`rating-star-${star}`}
                >
                  <Star
                    size={40}
                    weight={(hoverRating || rating) >= star ? "fill" : "regular"}
                    className={(hoverRating || rating) >= star ? "text-amber-500" : "text-zinc-300"}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-center text-sm text-zinc-500 mt-2">
                {rating === 5 && "Excellent! 🎉"}
                {rating === 4 && "Great! 😊"}
                {rating === 3 && "Good 👍"}
                {rating === 2 && "Fair 🤔"}
                {rating === 1 && "Poor 😕"}
              </p>
            )}
          </div>

          {/* Feedback Text */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">
              What could we have done better?
            </label>
            <Textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Please share the details of your experience so we can improve..."
              rows={4}
              className="border-2"
              data-testid="feedback-textarea"
            />
          </div>

          {/* Submit Button */}
          <Button
            onClick={submitFeedback}
            disabled={submitting || rating === 0}
            className="w-full h-12 bg-[#FF6600] hover:bg-[#CC5200] text-white text-lg"
            data-testid="submit-feedback-btn"
          >
            {submitting ? "Sending..." : "Send Feedback"}
          </Button>

          <p className="text-center text-xs text-zinc-400">
            Your feedback is private and will only be seen by {reviewData?.company_name}
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <div className="fixed bottom-0 left-0 right-0 py-4 bg-white/80 backdrop-blur border-t border-zinc-200">
      <p className="text-center text-xs text-zinc-400">
        Powered by <span className="font-semibold text-[#FF6600]">Free Service Pro</span>
      </p>
    </div>
  );
}
